import { useObserver } from "mobx-react-lite";
import useStores from "src/utils/use-stores";

export interface ChatSuggestion {
    title: string;
    message: string;
}

const CREATE_NEW_APP: ChatSuggestion = {
    title: "Create a new app",
    message: "Walk me through creating a new Striim application."
};

const SHOW_RUNNING: ChatSuggestion = {
    title: "Show me running apps",
    message: "Which of my Striim applications are currently running?"
};

const SHOW_ERRORS: ChatSuggestion = {
    title: "Show me apps with errors",
    message: "Which of my Striim applications have errors or are in a failed state?"
};

const FlowStatus = {
    CRASH: "CRASH",
    HALT: "HALT",
    TERMINATED: "TERMINATED",
    DEPLOY_FAILED: "DEPLOY_FAILED",
    STOPPED: "STOPPED",
    CREATED: "CREATED"
} as const;

const RUNNING_STATUSES = ["RUNNING", "RUNNING_UNTIL_END", "RUNNING_UNTIL_QUIESCE", "APPROVING_QUIESCE"];

export function useHomepageSuggestions(isActive: boolean): ChatSuggestion[] {
    const { store } = useStores() as { store: { apps: Array<{ name: string; flowStatus: string }> } };

    return useObserver(() => {
        if (!isActive) return [];

        const apps: Array<{ name: string; flowStatus: string }> = store.apps ?? [];

        if (apps.length === 0) return [CREATE_NEW_APP];

        const hasRunningApps = apps.some(a => RUNNING_STATUSES.includes(a.flowStatus?.toUpperCase()));

        const crashed = apps.find(a =>
            [FlowStatus.CRASH, FlowStatus.HALT, FlowStatus.TERMINATED].includes(
                a.flowStatus?.toUpperCase() as any
            )
        );
        if (crashed) {
            return [
                { title: `Why did ${crashed.name} crash?`, message: `Why did ${crashed.name} crash? Walk me through the error and how to fix it.` },
                ...(hasRunningApps ? [SHOW_RUNNING] : []),
                CREATE_NEW_APP
            ];
        }

        const deployFailed = apps.find(a => a.flowStatus?.toUpperCase() === FlowStatus.DEPLOY_FAILED);
        if (deployFailed) {
            return [
                { title: `Why did ${deployFailed.name} fail to deploy?`, message: `Why did ${deployFailed.name} fail to deploy? What's the root cause and how do I fix it?` },
                ...(hasRunningApps ? [SHOW_RUNNING] : []),
                CREATE_NEW_APP
            ];
        }

        const created = apps.find(a => a.flowStatus?.toUpperCase() === FlowStatus.CREATED);
        if (created) {
            return [
                { title: `Deploy and start ${created.name}`, message: `Deploy and start the application "${created.name}".` },
                ...(hasRunningApps ? [SHOW_RUNNING] : []),
                CREATE_NEW_APP
            ];
        }

        const stopped = apps.find(a => a.flowStatus?.toUpperCase() === FlowStatus.STOPPED);
        if (stopped) {
            return [
                { title: `Help restart ${stopped.name}`, message: `Help me safely restart ${stopped.name} and verify it's healthy after starting.` },
                ...(hasRunningApps ? [SHOW_RUNNING] : []),
                CREATE_NEW_APP
            ];
        }

        return [SHOW_RUNNING, SHOW_ERRORS, CREATE_NEW_APP];
    });
}
